import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { AlertCircle } from 'lucide-react';
import { Product, PRODUCT_TYPES, CollectionType, ProductCategory } from '../../types';
import { ImageGallery } from '../../components/admin/ImageGallery';
import { RichTextEditor } from '../../components/admin/RichTextEditor';
import { useToast } from '../../context/ToastContext';
import { supabaseUrl, supabaseAnonKey } from '../../utils/supabase';

const COLLECTION_TYPES: CollectionType[] = ['Feature Item', 'On Sale'];
const PRODUCT_CATEGORIES: ProductCategory[] = ['New', 'Used', 'Like New'];

const productSchema = Yup.object().shape({
  name: Yup.string().required('Product name is required'),
  brand: Yup.string().required('Brand is required'),
  type: Yup.string().oneOf([...PRODUCT_TYPES], 'Invalid product type').required('Type is required'),
  collectionType: Yup.string().oneOf(COLLECTION_TYPES, 'Invalid collection type').required('Collection type is required'),
  category: Yup.string().oneOf(PRODUCT_CATEGORIES, 'Invalid category').required('Category is required'),
  price: Yup.number().positive('Price must be positive').required('Price is required'),
  description: Yup.string().required('Description is required'),
  stockLevel: Yup.number().min(0, 'Stock cannot be negative').required('Stock level is required'),
  images: Yup.array().min(1, 'At least one image is required')
});

export function AdminNewProduct() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: any, formikHelpers: { setSubmitting: (isSubmitting: boolean) => void }, isDraft: boolean = false) => {
    try {
      if (!values.images || values.images.length === 0) {
        showToast('Please add at least one product image', 'error');
        return;
      }

      setIsSubmitting(true);

      console.log('💾 Creating product with data:', {
        ...values,
        isDraft,
        imageCount: values.images.length,
        imageUrls: values.images.map((img: any) => img.url)
      });

      const productData = {
        name: values.name,
        brand: values.brand,
        type: values.type,
        product_type: values.collectionType,
        category: values.category,
        price: Number(values.price),
        description: values.description,
        stock_level: Number(values.stockLevel),
        images: values.images,
        is_published: !isDraft,
        is_draft: isDraft,
        is_deleted: false,
        date_added: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('📤 Sending product data to API:', productData);

      const response = await fetch(`${supabaseUrl}/functions/v1/create-product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify(productData)
      });

      console.log('📡 Create product response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('❌ Create product failed:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to create product');
      }

      const result = await response.json();
      console.log('✅ Product created successfully:', result);

      showToast(
        isDraft ? 'Product saved as draft' : 'Product successfully published',
        'success'
      );
      navigate('/admin/inventory');
    } catch (error) {
      console.error('💥 Error saving product:', error);
      showToast(error instanceof Error ? error.message : 'Failed to save product', 'error');
    } finally {
      setIsSubmitting(false);
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Add New Product</h2>

          <Formik
            initialValues={{
              name: '',
              brand: '',
              type: '',
              collectionType: '',
              category: '',
              price: '',
              description: '',
              stockLevel: '',
              images: []
            }}
            validationSchema={productSchema}
            onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers, false)}
          >
            {({ errors, touched, setFieldValue, isValid, dirty, values }) => (
              <Form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Product Images *
                  </label>
                  <ImageGallery
                    images={values.images}
                    onImagesChange={(images) => {
                      console.log('🖼️ Images changed in form:', images);
                      setFieldValue('images', images);
                    }}
                  />
                  {errors.images && touched.images && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.images}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Product Name *
                    </label>
                    <Field
                      id="name"
                      name="name"
                      type="text"
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        errors.name && touched.name
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    />
                    {errors.name && touched.name && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.name}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="brand" className="block text-sm font-medium text-gray-700">
                      Brand *
                    </label>
                    <Field
                      id="brand"
                      name="brand"
                      type="text"
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        errors.brand && touched.brand
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    />
                    {errors.brand && touched.brand && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.brand}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Type *
                    </label>
                    <Field
                      as="select"
                      id="type"
                      name="type"
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        errors.type && touched.type
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    >
                      <option value="">Select Type</option>
                      {PRODUCT_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Field>
                    {errors.type && touched.type && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.type}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="collectionType" className="block text-sm font-medium text-gray-700">
                      Collection Type *
                    </label>
                    <Field
                      as="select"
                      id="collectionType"
                      name="collectionType"
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        errors.collectionType && touched.collectionType
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    >
                      <option value="">Select Collection Type</option>
                      {COLLECTION_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Field>
                    {errors.collectionType && touched.collectionType && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.collectionType}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                      Condition *
                    </label>
                    <Field
                      as="select"
                      id="category"
                      name="category"
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        errors.category && touched.category
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    >
                      <option value="">Select Condition</option>
                      {PRODUCT_CATEGORIES.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </Field>
                    {errors.category && touched.category && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.category}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                      Price *
                    </label>
                    <Field
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        errors.price && touched.price
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    />
                    {errors.price && touched.price && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.price}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700">
                      Stock Level *
                    </label>
                    <Field
                      id="stockLevel"
                      name="stockLevel"
                      type="number"
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        errors.stockLevel && touched.stockLevel
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    />
                    {errors.stockLevel && touched.stockLevel && (
                      <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.stockLevel}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <RichTextEditor
                    value={values.description}
                    onChange={(value) => setFieldValue('description', value)}
                    placeholder="Enter product description..."
                  />
                  {errors.description && touched.description && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.description}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/inventory')}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmit(values, { setSubmitting: (_isSubmitting: boolean) => {} }, true)}
                    disabled={!isValid || !dirty || isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Save as Draft
                  </button>
                  <button
                    type="submit"
                    disabled={!isValid || !dirty || isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Publishing...' : 'Publish Product'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}
